import clsx from 'clsx'
import { orderBy } from 'lodash-es'
import { type PropsWithChildren, useState } from 'react'

import { FvButton, type IconName } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'

import {
  type FilterType,
  MiniQuoteList,
} from '@/features/mini-quote-list/MiniQuoteList'

import { ActionList } from '../ActionList'
import { TabLink } from '../TabLink'
import { NoteList } from './NoteList'
import { ShipmentTags } from './ShipmentTags'

type MessageFilterBtnProps = PropsWithChildren<{
  active?: boolean
  onClick: () => void
  icon: IconName
}>
const MessageFilterBtn = ({
  icon,
  active,
  onClick,
  children,
}: MessageFilterBtnProps) => {
  return (
    <FvButton
      icon={icon}
      transform="down-1"
      onClick={onClick}
      className={clsx('px-3 py-1 rounded-full', {
        'bg-fv-blue-100 border border-fv-blue-500': active,
        'border border-fv-gray': !active,
      })}
    >
      {children}
    </FvButton>
  )
}

type PanelName = 'messages' | 'notes' | 'tags'
type MessagesNotesTagsPanelProps = {
  closePanel?: () => void
  load: FullShipment
  className?: string
  navClass?: string
  contentClass?: string
}

export const useMessagesNotesTagsState = () => {
  const [activePanel, setActivePanel] = useState<PanelName>('messages')
  const [messageFilter, setMessageFilter] = useState<FilterType>('active')

  return { activePanel, setActivePanel, messageFilter, setMessageFilter }
}
type MessagesNotesTagsState = ReturnType<typeof useMessagesNotesTagsState>

type MessagesNotesTagsNavProps = MessagesNotesTagsState & {
  closePanel?: () => void
  className?: string
}
export const MessagesNotesTagsNav = ({
  closePanel,
  activePanel,
  setActivePanel,
  className,
}: MessagesNotesTagsNavProps) => (
  <ActionList className={className}>
    {closePanel && (
      <FvButton icon="arrow-to-left" onClick={closePanel}>
        Back
      </FvButton>
    )}
    <TabLink
      onClick={() => setActivePanel('messages')}
      icon="comment-alt"
      isActive={activePanel === 'messages'}
      transform="down-1"
    >
      Messages
    </TabLink>
    <TabLink
      onClick={() => setActivePanel('notes')}
      icon="sticky-note"
      isActive={activePanel === 'notes'}
    >
      Notes
    </TabLink>
    <TabLink
      onClick={() => setActivePanel('tags')}
      icon="tags"
      isActive={activePanel === 'tags'}
    >
      Tags
    </TabLink>
  </ActionList>
)

type MessagesNotesTagsContentProps = MessagesNotesTagsState & {
  load: FullShipment
  className?: string
}
export const MessagesNotesTagsContent = ({
  load,
  activePanel,
  messageFilter,
  setMessageFilter,
  className,
}: MessagesNotesTagsContentProps) => {
  const { loadId, selectedQuote } = load

  return (
    <div className={clsx(className)}>
      {activePanel === 'messages' && (
        <>
          <div className="border gap-x-3 border-fv-gray p-2 rounded-full mb-4 flex [&>button]:ml-0 bg-fv-blue-25 fa-width-auto">
            <MessageFilterBtn
              icon="comment-alt-dots"
              onClick={() => setMessageFilter('active')}
              active={messageFilter === 'active'}
            >
              Active
            </MessageFilterBtn>
            <MessageFilterBtn
              icon="comment-alt-minus"
              onClick={() => setMessageFilter('pending')}
              active={messageFilter === 'pending'}
            >
              Inactive
            </MessageFilterBtn>
            <MessageFilterBtn
              icon="comment-alt-slash"
              onClick={() => setMessageFilter('declined')}
              active={messageFilter === 'declined'}
            >
              Declined
            </MessageFilterBtn>
          </div>

          <MiniQuoteList
            filter={messageFilter}
            key={selectedQuote?._id}
            load={load}
            readOnly
            initialActiveId={selectedQuote?._id}
            sort={r =>
              orderBy(
                r,
                [r => r.status === 'awarded', r => r.amount, r => r.name],
                ['desc', 'desc', 'asc'],
              )
            }
          />
        </>
      )}

      {activePanel === 'notes' && <NoteList loadId={loadId} />}

      {activePanel === 'tags' && <ShipmentTags loadId={loadId} />}
    </div>
  )
}

export const MessagesNotesTagsControls = ({
  closePanel,
  load,
  className,
  navClass,
  contentClass,
}: MessagesNotesTagsPanelProps) => {
  const state = useMessagesNotesTagsState()

  return (
    <>
      <MessagesNotesTagsNav
        closePanel={closePanel}
        className={navClass}
        {...state}
      />
      <MessagesNotesTagsContent
        load={load}
        className={contentClass}
        {...state}
      />
    </>
  )
}
